import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getProjectById, getStakeholderVisibility, updateStakeholderVisibility } from '../../../../../services/projectService';
import { isAdmin, isClient, isStakeholder } from '../../../../../utils/share';
import { PROJECT_EDITOR_ROLE } from '../../../../../Constants/ProjectConstants';
import { DEFAULT_OVERVIEW_VISIBILITY } from '../../../overview/constants/stakeholderVisibility';

// Creating a context
const ProjectContext = createContext({
  projectDetails: {},
  isLoading: false,
  isEditPermission: false,
  projectRole: '',
  refetchProject: () => {},
  setProjectDetails: () => {},
  stakeholderOverviewVisibility: DEFAULT_OVERVIEW_VISIBILITY,
  updateOverviewVisibility: () => {},
  canConfigureStakeholderVisibility: false,
  userIsStakeholder: false,
});

export const ProjectProvider = ({ children }) => {
  const { id } = useParams();
  const accountData = useSelector((state) => state?.login?.user || null);
  // get path name
  const { pathname } = useLocation();
  const [projectDetails, setProjectDetails] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [refetcher, setRefetcher] = useState(false);
  const [stakeholderOverviewVisibility, setStakeholderOverviewVisibility] = useState(DEFAULT_OVERVIEW_VISIBILITY);

  const refetchProject = useCallback(() => setRefetcher((prev) => !prev), []);

  const projectRole =
    projectDetails?.projectmember?.find((data) => data.userId === accountData?.id)?.projectRoles?.roleName ?? '';

  const userIsAdmin = isAdmin();
  const userIsClient = isClient();
  const userIsStakeholder = isStakeholder();

  const isEditPermission = isAdmin() || PROJECT_EDITOR_ROLE.includes(projectRole);
  const canConfigureStakeholderVisibility = userIsClient;

  const fetchStakeholderVisibility = useCallback(async () => {
    try {
      const response = await getStakeholderVisibility(id);
      const overviewVisibility = response?.data?.data?.stakeholderVisibility?.overview;
      setStakeholderOverviewVisibility({ ...DEFAULT_OVERVIEW_VISIBILITY, ...overviewVisibility });
    } catch (error) {
      setStakeholderOverviewVisibility(DEFAULT_OVERVIEW_VISIBILITY);
    }
  }, [id]);

  const updateOverviewVisibility = useCallback(
    async (overviewUpdates) => {
      await updateStakeholderVisibility(id, { overview: overviewUpdates });
      setStakeholderOverviewVisibility((prev) => ({ ...prev, ...overviewUpdates }));
    },
    [id]
  );

  useEffect(() => {
    const fetchProjectDetails = async () => {
      setLoading(true);
      try {
        const Projects = await getProjectById(id);
        setProjectDetails(Projects?.data?.data);
        await fetchStakeholderVisibility();
      } catch (e) {
        toast.error(e.message);
        setStakeholderOverviewVisibility(DEFAULT_OVERVIEW_VISIBILITY);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectDetails();
  }, [id, refetcher, pathname, fetchStakeholderVisibility]);

  return (
    <ProjectContext.Provider
      value={{
        projectDetails,
        refetchProject,
        isLoading,
        setProjectDetails,
        isEditPermission,
        projectRole,
        userIsAdmin,
        userIsClient,
        userIsStakeholder,
        stakeholderOverviewVisibility,
        updateOverviewVisibility,
        canConfigureStakeholderVisibility,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

ProjectProvider.propTypes = {
  children: PropTypes.node,
};
